import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowDownToLine,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  History,
  Landmark,
  Layers,
  Loader2,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Users,
  Wallet,
  Workflow,
  X,
  XCircle,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import LedgerReportButton from '../Ledger/LedgerReportButton';
import { useWindowSize } from '../../hooks/useWindowSize';
import ReconciliationKpiGrid from '../reconciliation/ReconciliationKpiGrid';
import ReconciliationTable from '../reconciliation/ReconciliationTable';
import ExportPreviewModal from '../reconciliation/ExportPreviewModal';
import { ReconToastProvider, useReconToast } from '../reconciliation/ReconToast';
import {
  buildEnterpriseXlsxFilename,
  computeReconciliationSummary,
  enrichTransactionRows,
  exportFilteredReconciliationCsv,
  exportFilteredReconciliationXlsx,
  loadExportHistory,
  pushExportHistory,
} from '../../utils/reconciliation';

const PAGE_SIZE = 25;

const CHANNEL_OPTIONS = [
  { value: 'all', label: 'All Channels', sub: 'Complete ledger', icon: Layers },
  { value: 'wallet_recharge', label: 'Wallet', sub: 'Recharge', icon: Wallet },
  { value: 'booking_advance', label: 'Booking', sub: 'Advance', icon: Calendar },
  { value: 'refund', label: 'Refund', sub: 'Reversed pay', icon: RotateCcw },
  { value: 'general', label: 'General', sub: 'Other', icon: Receipt },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status', icon: Filter },
  { value: 'captured', label: 'Captured', icon: CheckCircle2 },
  { value: 'failed', label: 'Failed', icon: XCircle },
  { value: 'reversed', label: 'Reversed', icon: RefreshCw },
];

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All', icon: Layers },
  { value: 'Client', label: 'Client', icon: Users },
  { value: 'Owner', label: 'Owner', icon: Building2 },
  { value: 'Treasury', label: 'Treasury', icon: Landmark },
  { value: 'Internal', label: 'Internal', icon: Workflow },
];

const DATE_PRESETS = [
  { id: '7d', label: '7 Days', icon: Calendar },
  { id: '30d', label: '30 Days', icon: CalendarDays },
  { id: 'month', label: 'This Month', icon: CalendarRange },
];

function FilterBlock({ title, icon: Icon, children }) {
  return (
    <div className="recon-filter-block">
      <p className="recon-filter-title">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </p>
      {children}
    </div>
  );
}

function OptionTile({ active, onClick, icon: Icon, label, sub }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('de-ledger-option', active ? 'de-ledger-option-active' : 'de-ledger-option-idle')}
    >
      <span className={cn('de-stat-ring h-8 w-8 shrink-0', active && 'border-de-gold/50')}>
        <Icon className={cn('h-4 w-4', active ? 'text-de-gold' : 'text-de-muted')} />
      </span>
      <span className="min-w-0">
        <span className="block truncate">{label}</span>
        {sub ? <span className="block truncate text-[10px] font-normal text-de-muted">{sub}</span> : null}
      </span>
    </button>
  );
}

function FilterChip({ label, onRemove }) {
  return (
    <span className="recon-filter-chip">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Remove ${label} filter`}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function TransactionLedgerInner({
  transactions,
  filteredTransactions,
  reconFrom,
  reconTo,
  onReconFromChange,
  onReconToChange,
  channelFilter,
  onChannelFilterChange,
  statusFilter,
  onStatusFilterChange,
  onLast7Days,
  onLast30Days,
  onThisMonth,
  hasMore,
  loadingMore,
  onLoadMore,
  loadMoreLabel,
  loadingLabel,
  inferTxnStatus,
  inferTxnChannel,
  embedded = false,
  ledgerClientLookup = {},
  ledgerMachineLookup = null,
  ledgerBookingByRef = {},
}) {
  const [datePreset, setDatePreset] = useState('month');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exportHistory, setExportHistory] = useState(() => loadExportHistory());
  const { isMobile, isTablet } = useWindowSize();
  const showPremiumHero = !embedded || isMobile || isTablet;
  const { push: pushToast } = useReconToast();

  const enrichCtx = useMemo(
    () => ({
      clientLookup: ledgerClientLookup,
      bookingByRef: ledgerBookingByRef,
      machineLookup: ledgerMachineLookup,
      inferChannel: inferTxnChannel,
      inferStatus: inferTxnStatus,
    }),
    [ledgerClientLookup, ledgerBookingByRef, ledgerMachineLookup, inferTxnChannel, inferTxnStatus],
  );

  const enrichedFiltered = useMemo(
    () => enrichTransactionRows(filteredTransactions, enrichCtx),
    [filteredTransactions, enrichCtx],
  );

  const searchFiltered = useMemo(() => {
    let rows = enrichedFiltered;
    if (categoryFilter !== 'all') {
      rows = rows.filter((row) => row.categoryLabel === categoryFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.receiptRef,
        row.categoryLabel,
        row.clientName,
        row.clientCompany,
        row.channelLabel,
        row.statusLabel,
        row.descriptionClean,
        row.machineCode,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [enrichedFiltered, categoryFilter, searchQuery]);

  const summary = useMemo(
    () => computeReconciliationSummary(searchFiltered, inferTxnChannel, inferTxnStatus),
    [searchFiltered, inferTxnChannel, inferTxnStatus],
  );

  const exportFilename = useMemo(
    () => buildEnterpriseXlsxFilename({ from: reconFrom, to: reconTo }),
    [reconFrom, reconTo],
  );

  const activeChips = useMemo(() => {
    const chips = [];
    if (channelFilter !== 'all') {
      chips.push({
        key: 'channel',
        label: CHANNEL_OPTIONS.find((o) => o.value === channelFilter)?.label || channelFilter,
        onRemove: () => onChannelFilterChange('all'),
      });
    }
    if (statusFilter !== 'all') {
      chips.push({
        key: 'status',
        label: STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter,
        onRemove: () => onStatusFilterChange('all'),
      });
    }
    if (categoryFilter !== 'all') {
      chips.push({
        key: 'category',
        label: CATEGORY_OPTIONS.find((o) => o.value === categoryFilter)?.label || categoryFilter,
        onRemove: () => setCategoryFilter('all'),
      });
    }
    if (searchQuery.trim()) {
      chips.push({
        key: 'search',
        label: `Search: "${searchQuery.trim()}"`,
        onRemove: () => setSearchQuery(''),
      });
    }
    return chips;
  }, [channelFilter, statusFilter, categoryFilter, searchQuery, onChannelFilterChange, onStatusFilterChange]);

  const handleExportConfirm = useCallback(async () => {
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 350));
      const result = await exportFilteredReconciliationXlsx(searchFiltered, enrichCtx, {
        from: reconFrom,
        to: reconTo,
      }, { preEnriched: true });
      const nextHistory = pushExportHistory({
        filename: result.filename,
        rowCount: result.rowCount,
        sheetCount: result.sheetCount,
        format: 'xlsx',
        from: reconFrom,
        to: reconTo,
      });
      setExportHistory(nextHistory);
      setPreviewOpen(false);
      pushToast({
        title: 'Export complete',
        message: `${result.rowCount} rows · 7 sheets · ${result.filename}`,
      });
    } catch (err) {
      pushToast({
        type: 'error',
        title: 'Export failed',
        message: err.message || 'Unable to generate Excel workbook',
      });
    } finally {
      setExporting(false);
    }
  }, [searchFiltered, enrichCtx, reconFrom, reconTo, pushToast]);

  const handleExportCsv = useCallback(async () => {
    setCsvExporting(true);
    try {
      const result = await exportFilteredReconciliationCsv(searchFiltered, enrichCtx, {
        from: reconFrom,
        to: reconTo,
      }, { preEnriched: true });
      const nextHistory = pushExportHistory({
        filename: result.filename,
        rowCount: result.rowCount,
        sheetCount: 1,
        format: 'csv',
        from: reconFrom,
        to: reconTo,
      });
      setExportHistory(nextHistory);
      pushToast({
        title: 'CSV export complete',
        message: `${result.rowCount} rows · ${result.filename}`,
      });
    } catch (err) {
      pushToast({
        type: 'error',
        title: 'CSV export failed',
        message: err.message || 'Unable to generate CSV file',
      });
    } finally {
      setCsvExporting(false);
    }
  }, [searchFiltered, enrichCtx, reconFrom, reconTo, pushToast]);

  return (
    <section className="recon-dashboard space-y-5">
      {!showPremiumHero && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Badge variant="muted" className="text-[10px] uppercase tracking-wider">
            Enterprise Reconciliation · GST 18%
          </Badge>
          <p className="m-0 text-sm font-bold text-de-gold">{summary.count} transactions</p>
        </div>
      )}

      {showPremiumHero && (
        <div className="recon-hero">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge variant="muted" className="text-[10px] uppercase tracking-wider">
                  Enterprise Reconciliation
                </Badge>
                <Badge className="border-emerald-500/30 bg-emerald-500/10 text-[10px] uppercase tracking-wider text-emerald-400">
                  Audit-ready
                </Badge>
              </div>
              <h3 className="m-0 flex items-center gap-2.5 text-xl font-black sm:text-2xl">
                <FileText className="h-7 w-7 text-de-gold" />
                <span className="de-shimmer-text">Financial Reconciliation</span>
              </h3>
              <p className="m-0 mt-2 max-w-2xl text-sm text-de-muted">
                Premium ledger dashboard with investor-grade exports — filter, analyse, and download
                reconciliation reports that match exactly what you see on screen.
              </p>
            </div>
            <div className="recon-hero-stat">
              <p className="m-0 text-[10px] uppercase tracking-wider text-de-muted">Filtered rows</p>
              <p className="m-0 mt-1 text-2xl font-black text-de-gold">{summary.count}</p>
            </div>
          </div>
        </div>
      )}

      <ReconciliationKpiGrid summary={summary} />

      <div className="de-ultra-card p-4 sm:p-5">
        <p className="m-0 mb-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-de-gold/80">
          <Sparkles className="h-3.5 w-3.5" />
          Filters & export controls
        </p>

        <div className="mb-3">
          <div className="recon-search-wrap">
            <Search className="h-4 w-4 text-de-muted" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search client, reference, channel, description…"
              className="recon-search-input"
              aria-label="Search transactions"
            />
          </div>
          {activeChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {activeChips.map((chip) => (
                <FilterChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <FilterBlock title="Payment channel" icon={Wallet}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3">
              {CHANNEL_OPTIONS.map((opt) => (
                <OptionTile
                  key={opt.value}
                  active={channelFilter === opt.value}
                  onClick={() => {
                    onChannelFilterChange(opt.value);
                    setPage(1);
                  }}
                  icon={opt.icon}
                  label={opt.label}
                  sub={opt.sub}
                />
              ))}
            </div>
          </FilterBlock>

          <FilterBlock title="Transaction status" icon={CheckCircle2}>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <OptionTile
                  key={opt.value}
                  active={statusFilter === opt.value}
                  onClick={() => {
                    onStatusFilterChange(opt.value);
                    setPage(1);
                  }}
                  icon={opt.icon}
                  label={opt.label}
                />
              ))}
            </div>
          </FilterBlock>
        </div>

        <div className="mt-3">
          <FilterBlock title="Party category" icon={Layers}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              {CATEGORY_OPTIONS.map((opt) => (
                <OptionTile
                  key={opt.value}
                  active={categoryFilter === opt.value}
                  onClick={() => {
                    setCategoryFilter(opt.value);
                    setPage(1);
                  }}
                  icon={opt.icon}
                  label={opt.label}
                />
              ))}
            </div>
          </FilterBlock>
        </div>

        <div className="mt-3">
          <FilterBlock title="Reporting period" icon={CalendarRange}>
            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <OptionTile
                  key={preset.id}
                  active={datePreset === preset.id}
                  onClick={() => {
                    setDatePreset(preset.id);
                    setPage(1);
                    if (preset.id === '7d') onLast7Days();
                    else if (preset.id === '30d') onLast30Days();
                    else onThisMonth();
                  }}
                  icon={preset.icon}
                  label={preset.label}
                />
              ))}
            </div>
          </FilterBlock>
        </div>

        <div className="mt-3 recon-date-panel">
          <p className="m-0 mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-de-gold">
            <CalendarRange className="h-3.5 w-3.5" />
            Custom date range
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label htmlFor="recon-from" className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-de-muted">
                From
              </label>
              <input
                id="recon-from"
                type="date"
                value={reconFrom}
                onChange={(e) => {
                  setDatePreset('custom');
                  setPage(1);
                  onReconFromChange(e.target.value);
                }}
                className="de-ledger-date sm:max-w-[200px]"
              />
            </div>
            <span className="hidden pb-3 text-de-gold sm:inline">→</span>
            <div className="flex-1">
              <label htmlFor="recon-to" className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-de-muted">
                To
              </label>
              <input
                id="recon-to"
                type="date"
                value={reconTo}
                onChange={(e) => {
                  setDatePreset('custom');
                  setPage(1);
                  onReconToChange(e.target.value);
                }}
                className="de-ledger-date sm:max-w-[200px]"
              />
            </div>
            {datePreset === 'custom' ? (
              <Badge className="self-center border-de-gold/40 bg-de-gold/10 text-de-gold">Custom range</Badge>
            ) : null}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <button
            type="button"
            className="recon-export-btn"
            onClick={() => setPreviewOpen(true)}
            disabled={exporting || csvExporting || searchFiltered.length === 0}
          >
            {exporting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowDownToLine className="h-5 w-5" />}
            <span className="text-left">
              <span className="block">Export Excel Report</span>
              <span className="block text-[10px] font-normal opacity-80">
                {searchFiltered.length} rows · 7-sheet workbook
              </span>
            </span>
          </button>

          <button
            type="button"
            className="recon-export-btn"
            onClick={handleExportCsv}
            disabled={exporting || csvExporting || searchFiltered.length === 0}
          >
            {csvExporting ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
            <span className="text-left">
              <span className="block">Export CSV</span>
              <span className="block text-[10px] font-normal opacity-80">
                {searchFiltered.length} rows · mobile friendly
              </span>
            </span>
          </button>

          <div className="recon-pdf-panel">
            <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-wider text-de-gold/80">
              Advance Ledger PDF
            </p>
            <LedgerReportButton
              period={{ start: reconFrom, end: reconTo }}
              sourceTransactions={filteredTransactions}
              clientLookup={ledgerClientLookup}
              machineLookup={ledgerMachineLookup}
              bookingByRef={ledgerBookingByRef}
              variant="auto"
            />
          </div>

          <button
            type="button"
            className="recon-history-btn"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <History className="h-5 w-5 shrink-0" />
            <span className="text-left">
              <span className="block">Download history</span>
              <span className="block text-[10px] font-normal opacity-80">
                {exportHistory.length} recent exports
              </span>
            </span>
          </button>
        </div>

        {historyOpen && exportHistory.length > 0 && (
          <div className="mt-3 recon-history-panel">
            <p className="m-0 mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-de-gold/75">
              <Clock className="h-3.5 w-3.5" />
              Recent exports
            </p>
            <ul className="m-0 list-none space-y-2 p-0">
              {exportHistory.map((item) => (
                <li key={item.id} className="recon-history-item">
                  <div className="min-w-0">
                    <p className="m-0 truncate font-mono text-xs text-de-gold">{item.filename}</p>
                    <p className="m-0 text-[10px] text-de-muted">
                      {item.rowCount} rows · {new Date(item.at).toLocaleString('en-IN')}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <ReconciliationTable
        rows={searchFiltered}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        totalLoaded={transactions.length}
        hasMore={hasMore}
      />

      {hasMore && (
        <div className="de-ultra-card px-4 py-3">
          <button
            type="button"
            disabled={loadingMore}
            onClick={onLoadMore}
            className="de-ledger-action w-full border-de-gold/40 bg-gradient-to-r from-de-gold/25 to-de-gold/5 text-de-gold disabled:opacity-50 md:mx-auto md:w-auto"
          >
            {loadingMore ? loadingLabel : loadMoreLabel}
          </button>
        </div>
      )}

      <ExportPreviewModal
        open={previewOpen}
        onClose={() => !exporting && !csvExporting && setPreviewOpen(false)}
        onConfirm={handleExportConfirm}
        onExportCsv={handleExportCsv}
        exporting={exporting}
        csvExporting={csvExporting}
        summary={summary}
        previewRows={searchFiltered}
        filename={exportFilename}
      />
    </section>
  );
}

export default function TransactionLedger(props) {
  return (
    <ReconToastProvider>
      <TransactionLedgerInner {...props} />
    </ReconToastProvider>
  );
}
